import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api.js';
import { EnvVarsEditor } from './EnvVarsEditor.js';
import './stacks.css';
import './stackdetail.css';

interface Droplet {
  id: string;
  name: string;
  ipv4: string | null;
  state: string;
  region: string | null;
  size: string | null;
  hostKeyPublic: string | null;
  hostKeyFingerprint: string | null;
}

interface EnvDetail {
  id: string;
  name: string;
  kind: 'production_pair_member' | 'standalone';
  status: string;
  deployedSha: string | null;
  deployedAt: string | null;
  appPort: number;
  healthPath: string;
  isActiveProd: boolean;
  droplets: Droplet[];
}

interface DeploymentRow {
  id: string;
  environmentId: string;
  kind: string;
  gitRef: string;
  gitSha: string;
  status: string;
  ranMigrations: boolean;
  createdAt: string;
}

interface StackDetail {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  repoOwner: string | null;
  repoName: string | null;
  appType: 'node' | 'python' | null;
  repoSubdir: string | null;
  buildCommand: string | null;
  runCommand: string | null;
  migrateCommand: string | null;
  frontingMode: 'none' | 'nginx' | 'do_lb' | null;
  domain: string | null;
  tlsEmail: string | null;
  backupBucket: string | null;
  backupRegion: string | null;
  doLbId: string | null;
  activeProdEnvId: string | null;
  cloudflareZoneId: string | null;
  dnsApexHost: string;
  environments: EnvDetail[];
  unassignedDroplets: Droplet[];
  recentDeployments: DeploymentRow[];
}

interface DnsSyncResult {
  zoneName: string;
  records: Array<{ name: string; content: string; action: 'created' | 'kept' | 'deleted' }>;
}

const STATUS_CLASS: Record<string, string> = {
  healthy: 'ok',
  deploying: 'warn',
  queued: 'warn',
  running: 'warn',
  succeeded: 'ok',
  unprovisioned: 'neutral',
};

function pill(status: string) {
  return <span className={`env-status status-${STATUS_CLASS[status] ?? 'err'}`}>{status}</span>;
}

function AdoptDropletButton({ stackId, droplet }: { stackId: string; droplet: Droplet }) {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<{ fingerprint: string; keyB64: string } | null>(null);
  const [error, setError] = useState('');

  const close = () => {
    setPreview(null);
    setError('');
  };

  const previewMut = useMutation({
    mutationFn: () =>
      apiFetch<{ dropletId: string; fingerprint: string; keyB64: string }>(
        `/stacks/${stackId}/droplets/${droplet.id}/adopt/preview`,
        { method: 'POST' },
      ),
    onSuccess: (r) => {
      setError('');
      setPreview({ fingerprint: r.fingerprint, keyB64: r.keyB64 });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'host-key probe failed'),
  });

  const confirmMut = useMutation({
    mutationFn: () =>
      apiFetch(`/stacks/${stackId}/droplets/${droplet.id}/adopt/confirm`, {
        method: 'POST',
        body: { keyB64: preview!.keyB64 },
      }),
    onSuccess: () => {
      close();
      void queryClient.invalidateQueries({ queryKey: ['stacks', stackId] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'adoption failed'),
  });

  const open = preview !== null || error !== '';

  return (
    <>
      <button className="btn" onClick={() => previewMut.mutate()} disabled={previewMut.isPending}>
        {previewMut.isPending ? 'Probing…' : 'Adopt'}
      </button>
      {open ? (
        <div className="dialog-backdrop" onClick={close}>
          <div className="card dialog" onClick={(e) => e.stopPropagation()}>
            <h2>
              Adopt <span className="mono">{droplet.name}</span>
            </h2>
            {error ? <p className="dim">{error}</p> : null}
            {preview ? (
              <>
                <p className="dim">Verify this host-key fingerprint matches the droplet before trusting it:</p>
                <p className="mono">SHA256:{preview.fingerprint}</p>
                <div className="dialog-actions">
                  <button className="btn" onClick={close}>
                    Cancel
                  </button>
                  <button className="btn btn-amber" onClick={() => confirmMut.mutate()} disabled={confirmMut.isPending}>
                    {confirmMut.isPending ? 'Adopting…' : 'Confirm & adopt'}
                  </button>
                </div>
              </>
            ) : (
              <div className="dialog-actions">
                <button className="btn btn-amber" onClick={close}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

export function StackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: stack, error } = useQuery<StackDetail>({
    queryKey: ['stacks', id],
    queryFn: () => apiFetch(`/stacks/${id}`),
    refetchInterval: 10_000,
  });
  const [deployEnv, setDeployEnv] = useState<EnvDetail | null>(null);
  const [switchTarget, setSwitchTarget] = useState<EnvDetail | null>(null);
  const [envVarsFor, setEnvVarsFor] = useState<EnvDetail | null>(null);
  const [attachEnv, setAttachEnv] = useState<EnvDetail | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [dnsResult, setDnsResult] = useState<DnsSyncResult | null>(null);

  const dnsSync = useMutation({
    mutationFn: () => apiFetch<DnsSyncResult>(`/stacks/${id}/dns/sync`, { method: 'POST' }),
    onSuccess: (r) => setDnsResult(r),
  });

  if (error) return <p className="error-text">{(error as Error).message}</p>;
  if (!stack) return <p className="mono dim">loading…</p>;

  return (
    <div>
      <header className="page-header">
        <div>
          <p className="page-eyebrow">Stack</p>
          <h1>{stack.name}</h1>
          <p className="mono dim" style={{ margin: '4px 0 0' }}>
            {stack.slug}
            {stack.repoOwner ? ` · ${stack.repoOwner}/${stack.repoName}` : ''}
            {stack.doLbId ? ` · LB ${stack.doLbId.slice(0, 8)}…` : ' · no load balancer'}
          </p>
        </div>
        <div className="page-actions">
          {stack.cloudflareZoneId ? (
            <button className="btn btn-ghost" onClick={() => dnsSync.mutate()} disabled={dnsSync.isPending}>
              {dnsSync.isPending ? 'Syncing DNS…' : 'Sync DNS'}
            </button>
          ) : null}
          <button className="btn btn-ghost" onClick={() => setShowSettings(true)}>
            Settings
          </button>
          <Link to="/stacks" className="btn btn-ghost">
            All stacks
          </Link>
        </div>
      </header>

      {dnsSync.error ? <p className="error-text">{(dnsSync.error as Error).message}</p> : null}
      {dnsResult ? (
        <div className="card dns-result">
          <h3>
            DNS synced — <span className="mono">{dnsResult.zoneName}</span>
          </h3>
          {dnsResult.records.length === 0 ? (
            <p className="dim">Nothing to manage yet — environments need active droplets with IPs.</p>
          ) : (
            <ul className="mono">
              {dnsResult.records.map((r, i) => (
                <li key={i} className={`dns-${r.action}`}>
                  {r.action.toUpperCase().padEnd(8)} {r.name} → {r.content}
                </li>
              ))}
            </ul>
          )}
          <button className="btn btn-ghost" onClick={() => setDnsResult(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="env-columns">
        {stack.environments.map((env) => (
          <div key={env.id} className={`card env-column ${env.isActiveProd ? 'env-column-active' : ''}`}>
            <div className="env-column-head">
              <span className="mono env-column-name">
                {env.name}
                {env.isActiveProd ? <span className="env-active">ACTIVE</span> : null}
              </span>
              {pill(env.status)}
            </div>
            <dl className="env-meta">
              <dt>Deployed</dt>
              <dd className="mono">{env.deployedSha ? env.deployedSha.slice(0, 12) : '—'}</dd>
              <dt>Health</dt>
              <dd className="mono">
                :{env.appPort}
                {env.healthPath}
              </dd>
            </dl>
            <div className="env-droplet-list">
              {env.droplets.length === 0 ? <p className="dim">no droplets tagged env:{env.name}</p> : null}
              {env.droplets.map((d) => (
                <div key={d.id} className="env-droplet mono">
                  <span>{d.name}</span>
                  <span className="dim">{d.ipv4 ?? 'no ip'}</span>
                  {pill(d.state)}
                  {!d.hostKeyPublic ? <AdoptDropletButton stackId={stack.id} droplet={d} /> : null}
                </div>
              ))}
            </div>
            <div className="env-column-actions">
              <button className="btn btn-amber" onClick={() => setDeployEnv(env)} disabled={env.droplets.length === 0}>
                Deploy
              </button>
              <button className="btn btn-ghost" onClick={() => setEnvVarsFor(env)}>
                Env vars
              </button>
              <button className="btn btn-ghost" onClick={() => setAttachEnv(env)}>
                Attach droplet
              </button>
              {env.kind === 'production_pair_member' && !env.isActiveProd && stack.doLbId ? (
                <button className="btn btn-ghost btn-switch" onClick={() => setSwitchTarget(env)}>
                  Make active
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {stack.unassignedDroplets.length > 0 ? (
        <div className="card unassigned-card">
          <h3>Unassigned droplets</h3>
          <p className="dim">Tagged for this stack but missing a recognized env tag.</p>
          {stack.unassignedDroplets.map((d) => (
            <div key={d.id} className="env-droplet mono">
              <span>{d.name}</span>
              <span className="dim">{d.ipv4}</span>
              {pill(d.state)}
            </div>
          ))}
        </div>
      ) : null}

      <FrontingPanel stack={stack} />
      <BackupsPanel stack={stack} />

      <section className="deploy-history">
        <h2>Recent deployments</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Environment</th>
              <th>Ref</th>
              <th>SHA</th>
              <th>Migrations</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {stack.recentDeployments.map((d) => {
              const env = stack.environments.find((e) => e.id === d.environmentId);
              return (
                <tr key={d.id} className="clickable" onClick={() => navigate(`/deployments/${d.id}`)}>
                  <td>{new Date(d.createdAt).toLocaleString()}</td>
                  <td className="mono">{env?.name ?? '?'}</td>
                  <td className="mono">{d.gitRef}</td>
                  <td className="mono">{d.gitSha.slice(0, 8)}</td>
                  <td>{d.ranMigrations ? 'yes' : '—'}</td>
                  <td>{pill(d.status)}</td>
                </tr>
              );
            })}
            {stack.recentDeployments.length === 0 ? (
              <tr>
                <td colSpan={6} className="dim">
                  No deployments yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      {deployEnv ? (
        <DeployDialog
          stack={stack}
          env={deployEnv}
          onClose={() => setDeployEnv(null)}
          onStarted={(deploymentId) => navigate(`/deployments/${deploymentId}`)}
        />
      ) : null}
      {switchTarget ? (
        <SwitchDialog
          stack={stack}
          target={switchTarget}
          onClose={() => {
            setSwitchTarget(null);
            void queryClient.invalidateQueries({ queryKey: ['stacks', id] });
          }}
        />
      ) : null}
      {envVarsFor ? <EnvVarsEditor env={envVarsFor} onClose={() => setEnvVarsFor(null)} /> : null}
      {attachEnv ? (
        <AttachDropletDialog
          stack={stack}
          env={attachEnv}
          onClose={() => {
            setAttachEnv(null);
            void queryClient.invalidateQueries({ queryKey: ['stacks', id] });
          }}
        />
      ) : null}
      {showSettings ? (
        <StackSettingsDialog
          stack={stack}
          onClose={() => {
            setShowSettings(false);
            void queryClient.invalidateQueries({ queryKey: ['stacks', id] });
          }}
        />
      ) : null}
    </div>
  );
}

function NginxFronting({ stack, env }: { stack: StackDetail; env: EnvDetail }) {
  const [runId, setRunId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const run = useQuery<{ status: string; log: string } | null>({
    queryKey: ['fronting-run', stack.id, runId],
    queryFn: () => apiFetch(`/stacks/${stack.id}/fronting/runs/${runId}`),
    enabled: runId !== null,
    refetchInterval: (q) => (q.state.data && q.state.data.status === 'running' ? 1500 : false),
  });

  const start = useMutation({
    mutationFn: () =>
      apiFetch<{ runId: string }>(`/stacks/${stack.id}/fronting/nginx`, {
        method: 'POST',
        body: { environmentId: env.id },
      }),
    onSuccess: (r) => { setError(''); setRunId(r.runId); },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'failed to start nginx config'),
  });

  const busy = start.isPending || run.data?.status === 'running';

  return (
    <div className="card" style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <span className="mono">{env.name}</span>
        <button className="btn btn-amber" disabled={busy} onClick={() => start.mutate()}>
          {busy ? 'Running…' : 'Configure nginx + TLS'}
        </button>
      </div>
      {error ? <p className="error-text">{error}</p> : null}
      {runId ? (
        <>
          <p className="mono">status: {run.data?.status ?? 'starting…'}</p>
          <pre className="mono" style={{ maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {run.data?.log ?? ''}
          </pre>
        </>
      ) : null}
    </div>
  );
}

function DoLbFronting({ stack }: { stack: StackDetail }) {
  const [result, setResult] = useState<{ id: string; ip: string; status: string } | null>(null);
  const [error, setError] = useState('');

  const provision = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string; ip: string; status: string }>(`/stacks/${stack.id}/fronting/load-balancer`, {
        method: 'POST',
      }),
    onSuccess: (r) => { setError(''); setResult(r); },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'failed to provision load balancer'),
  });

  return (
    <div>
      <button className="btn btn-amber" disabled={provision.isPending} onClick={() => provision.mutate()}>
        {provision.isPending ? 'Provisioning…' : 'Provision / reconcile load balancer'}
      </button>
      {error ? <p className="error-text">{error}</p> : null}
      {result ? (
        <dl className="env-meta" style={{ marginTop: 12 }}>
          <dt>ID</dt>
          <dd className="mono">{result.id}</dd>
          <dt>IP</dt>
          <dd className="mono">{result.ip}</dd>
          <dt>Status</dt>
          <dd className="mono">{result.status}</dd>
        </dl>
      ) : null}
    </div>
  );
}

function FrontingPanel({ stack }: { stack: StackDetail }) {
  const mode = stack.frontingMode ?? 'none';
  if (mode === 'none') return null;

  return (
    <section className="deploy-history" style={{ marginTop: 24 }}>
      <h2>Fronting</h2>
      {stack.domain ? (
        <p className="mono dim" style={{ marginBottom: 12 }}>
          {stack.domain}{stack.tlsEmail ? ` · ${stack.tlsEmail}` : ''}
        </p>
      ) : null}
      {mode === 'nginx' ? (
        <div>
          {stack.environments.map((env) => (
            <NginxFronting key={env.id} stack={stack} env={env} />
          ))}
        </div>
      ) : null}
      {mode === 'do_lb' ? <DoLbFronting stack={stack} /> : null}
    </section>
  );
}

interface BackupRow {
  id: string;
  kind: string;
  status: string;
  objectKey: string | null;
  startedAt: string;
  finishedAt: string | null;
  sizeBytes: number | null;
}

function RestoreDialog({
  stack,
  backupId,
  onClose,
}: {
  stack: StackDetail;
  backupId: string;
  onClose: () => void;
}) {
  const [confirmName, setConfirmName] = useState('');
  const [envId, setEnvId] = useState(stack.environments[0]?.id ?? '');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const restore = useMutation({
    mutationFn: () =>
      apiFetch(`/stacks/${stack.id}/backups/${backupId}/restore`, {
        method: 'POST',
        body: { environmentId: envId, confirmName },
      }),
    onSuccess: () => setDone(true),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'restore failed'),
  });

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="card dialog" onClick={(e) => e.stopPropagation()}>
        <h2>Restore backup</h2>
        {done ? (
          <>
            <p>Restore has been queued. Monitor your environment for progress.</p>
            <div className="dialog-actions">
              <button className="btn btn-amber" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p className="dim" style={{ color: 'var(--color-warn, #c47b00)' }}>
              Warning: restoring a backup will overwrite the target environment's data. This action cannot be undone.
            </p>
            <div className="field">
              <label>Target environment</label>
              <select className="dialog-select" value={envId} onChange={(e) => setEnvId(e.target.value)}>
                {stack.environments.map((env) => (
                  <option key={env.id} value={env.id}>{env.name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Type <span className="mono">{stack.name}</span> to confirm</label>
              <input className="mono" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} autoFocus />
            </div>
            <p className="error-text">{error}</p>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-amber"
                disabled={confirmName !== stack.name || restore.isPending}
                onClick={() => restore.mutate()}
              >
                {restore.isPending ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BackupsPanel({ stack }: { stack: StackDetail }) {
  const queryClient = useQueryClient();
  const [backupEnvId, setBackupEnvId] = useState(stack.environments[0]?.id ?? '');
  const [backupId, setBackupId] = useState<string | null>(null);
  const [runError, setRunError] = useState('');
  const [restoreBackupId, setRestoreBackupId] = useState<string | null>(null);

  const run = useQuery<{ status: string; log: string } | null>({
    queryKey: ['backup-run', stack.id, backupId],
    queryFn: () => apiFetch(`/stacks/${stack.id}/backups/${backupId}`),
    enabled: backupId !== null,
    refetchInterval: (q) => (q.state.data && q.state.data.status === 'running' ? 1500 : false),
  });

  const startBackup = useMutation({
    mutationFn: () =>
      apiFetch<{ backupId: string }>(`/stacks/${stack.id}/backups`, {
        method: 'POST',
        body: { environmentId: backupEnvId },
      }),
    onSuccess: (r) => {
      setRunError('');
      setBackupId(r.backupId);
    },
    onError: (err) => setRunError(err instanceof ApiError ? err.message : 'failed to start backup'),
  });

  const { data: backups } = useQuery<BackupRow[]>({
    queryKey: ['backups', stack.id],
    queryFn: () => apiFetch(`/stacks/${stack.id}/backups`),
  });

  // Invalidate the backups list when a run finishes
  const runStatus = run.data?.status;
  useEffect(() => {
    if (runStatus && runStatus !== 'running') {
      void queryClient.invalidateQueries({ queryKey: ['backups', stack.id] });
    }
  }, [runStatus, queryClient, stack.id]);

  if (!stack.backupBucket) return null;

  return (
    <section className="deploy-history" style={{ marginTop: 24 }}>
      <h2>Backups</h2>
      <p className="mono dim" style={{ marginBottom: 12 }}>
        {stack.backupBucket}{stack.backupRegion ? ` · ${stack.backupRegion}` : ''}
      </p>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 10px' }}>Run backup</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <select
            className="dialog-select"
            value={backupEnvId}
            onChange={(e) => setBackupEnvId(e.target.value)}
          >
            {stack.environments.map((env) => (
              <option key={env.id} value={env.id}>{env.name}</option>
            ))}
          </select>
          <button
            className="btn btn-amber"
            disabled={startBackup.isPending || run.data?.status === 'running'}
            onClick={() => startBackup.mutate()}
          >
            {startBackup.isPending || run.data?.status === 'running' ? 'Running…' : 'Back up now'}
          </button>
        </div>
        {runError ? <p className="error-text">{runError}</p> : null}
        {backupId ? (
          <>
            <p className="mono">status: {run.data?.status ?? 'starting…'}</p>
            <pre className="mono" style={{ maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {run.data?.log ?? ''}
            </pre>
          </>
        ) : null}
      </div>

      <h3>Backup history</h3>
      <table className="data-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Kind</th>
            <th>Status</th>
            <th>Object key</th>
            <th>Size</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(backups ?? []).map((b) => (
            <tr key={b.id}>
              <td>{new Date(b.startedAt).toLocaleString()}</td>
              <td className="mono">{b.kind}</td>
              <td>{pill(b.status)}</td>
              <td className="mono">{b.objectKey ?? '—'}</td>
              <td className="mono">{b.sizeBytes != null ? `${(b.sizeBytes / 1024 / 1024).toFixed(1)} MB` : '—'}</td>
              <td>
                {b.kind === 'backup' && b.status === 'done' ? (
                  <button className="btn btn-ghost" style={{ color: 'var(--color-warn, #c47b00)' }} onClick={() => setRestoreBackupId(b.id)}>
                    Restore
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
          {(backups ?? []).length === 0 ? (
            <tr>
              <td colSpan={6} className="dim">No backups yet.</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {restoreBackupId ? (
        <RestoreDialog
          stack={stack}
          backupId={restoreBackupId}
          onClose={() => setRestoreBackupId(null)}
        />
      ) : null}
    </section>
  );
}

function AttachDropletDialog({
  stack,
  env,
  onClose,
}: {
  stack: StackDetail;
  env: EnvDetail;
  onClose: () => void;
}) {
  const { data: allDroplets } = useQuery<Array<{ id: number; name: string; publicIp: string | null; status: string; tags: string[] }>>({
    queryKey: ['do', 'droplets'],
    queryFn: () => apiFetch('/do/droplets'),
  });
  const [selected, setSelected] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ needsAdoption: boolean; dropletName: string } | null>(null);

  const inThisEnv = new Set(
    stack.environments.flatMap((e) => e.droplets.map((d) => d.name)),
  );
  const candidates = (allDroplets ?? []).filter((d) => !inThisEnv.has(d.name));

  const attach = useMutation({
    mutationFn: () =>
      apiFetch<{ needsAdoption: boolean; dropletName: string }>(
        `/stacks/${stack.id}/environments/${env.id}/droplets`,
        { method: 'POST', body: { doDropletId: selected } },
      ),
    onSuccess: (r) => setResult(r),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'failed to attach'),
  });

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="card dialog" onClick={(e) => e.stopPropagation()}>
        <h2>
          Attach droplet to <span className="mono">{env.name}</span>
        </h2>
        {result ? (
          <>
            <p>
              <span className="mono">{result.dropletName}</span> is now tagged{' '}
              <span className="mono">stack:{stack.slug}</span> + <span className="mono">env:{env.name}</span>.
            </p>
            {result.needsAdoption ? (
              <p className="dim">
                Note: this droplet has no verified SSH host key yet, so it can serve traffic but can't
                receive deployments until it's adopted (host-key verification).
              </p>
            ) : null}
            <div className="dialog-actions">
              <button className="btn btn-amber" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="dim">
              Tags the droplet <span className="mono">env:{env.name}</span> so discovery, DNS, and the
              load-balancer switch treat it as part of this environment.
            </p>
            <div className="attach-list">
              {candidates.length === 0 ? <p className="dim">No other droplets found in DigitalOcean.</p> : null}
              {candidates.map((d) => (
                <label key={d.id} className="attach-row">
                  <input
                    type="radio"
                    name="droplet"
                    checked={selected === d.id}
                    onChange={() => setSelected(d.id)}
                  />
                  <span className="mono">{d.name}</span>
                  <span className="dim mono">{d.publicIp ?? 'no ip'}</span>
                  <span className="dim">{d.status}</span>
                </label>
              ))}
            </div>
            <p className="error-text">{error}</p>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button className="btn btn-amber" disabled={!selected || attach.isPending} onClick={() => attach.mutate()}>
                {attach.isPending ? 'Attaching…' : 'Attach'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StackSettingsDialog({ stack, onClose }: { stack: StackDetail; onClose: () => void }) {
  const { data: zones } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['dns', 'zones'],
    queryFn: () => apiFetch('/dns/zones'),
    retry: false,
  });
  const [zoneId, setZoneId] = useState(stack.cloudflareZoneId ?? '');
  const [apexHost, setApexHost] = useState(stack.dnsApexHost);
  const [lbId, setLbId] = useState(stack.doLbId ?? '');
  const [appType, setAppType] = useState<'node' | 'python'>(stack.appType ?? 'node');
  const [repoSubdir, setRepoSubdir] = useState(stack.repoSubdir ?? '');
  const [buildCommand, setBuildCommand] = useState(stack.buildCommand ?? '');
  const [runCommand, setRunCommand] = useState(stack.runCommand ?? '');
  const [migrateCommand, setMigrateCommand] = useState(stack.migrateCommand ?? '');
  const [frontingMode, setFrontingMode] = useState<'none' | 'nginx' | 'do_lb'>(stack.frontingMode ?? 'none');
  const [domain, setDomain] = useState(stack.domain ?? '');
  const [tlsEmail, setTlsEmail] = useState(stack.tlsEmail ?? '');
  const [backupBucket, setBackupBucket] = useState(stack.backupBucket ?? '');
  const [backupRegion, setBackupRegion] = useState(stack.backupRegion ?? '');
  const [error, setError] = useState('');

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/stacks/${stack.id}`, {
        method: 'PATCH',
        body: {
          cloudflareZoneId: zoneId || null,
          dnsApexHost: apexHost || '@',
          doLbId: lbId || null,
          appType,
          repoSubdir: repoSubdir || null,
          buildCommand: buildCommand || null,
          runCommand: runCommand || null,
          migrateCommand: migrateCommand || null,
          frontingMode,
          domain: domain || null,
          tlsEmail: tlsEmail || null,
          backupBucket: backupBucket || null,
          backupRegion: backupRegion || null,
        },
      }),
    onSuccess: onClose,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'failed to save'),
  });

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="card dialog" onClick={(e) => e.stopPropagation()}>
        <h2>Stack settings</h2>
        <div className="field">
          <label>Cloudflare zone (for {`<env>`}.domain records)</label>
          <select className="dialog-select" value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
            <option value="">— none —</option>
            {(zones ?? []).map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Apex host (@ = zone root; the record the LB serves)</label>
          <input className="mono" value={apexHost} onChange={(e) => setApexHost(e.target.value)} placeholder="@" />
        </div>
        <div className="field">
          <label>DigitalOcean load balancer ID</label>
          <input className="mono" value={lbId} onChange={(e) => setLbId(e.target.value)} placeholder="lb id (blue/gold switch target)" />
        </div>
        <div className="field">
          <label>App type</label>
          <select className="dialog-select" value={appType} onChange={(e) => setAppType(e.target.value as 'node' | 'python')}>
            <option value="node">node</option>
            <option value="python">python</option>
          </select>
        </div>
        <div className="field">
          <label>Repo subdirectory (optional)</label>
          <input className="mono" value={repoSubdir} onChange={(e) => setRepoSubdir(e.target.value)} placeholder="e.g. backend" />
        </div>
        <div className="field">
          <label>Build command (optional)</label>
          <input className="mono" value={buildCommand} onChange={(e) => setBuildCommand(e.target.value)} placeholder="e.g. npm run build" />
        </div>
        <div className="field">
          <label>Run command (optional)</label>
          <input className="mono" value={runCommand} onChange={(e) => setRunCommand(e.target.value)} placeholder="e.g. node dist/server.js" />
          {appType === 'python' ? <p className="dim">Python apps require a run command.</p> : null}
        </div>
        <div className="field">
          <label>Migrate command (optional)</label>
          <input className="mono" value={migrateCommand} onChange={(e) => setMigrateCommand(e.target.value)} placeholder="e.g. python manage.py migrate" />
        </div>
        <div className="field">
          <label>Fronting mode</label>
          <select className="dialog-select" value={frontingMode} onChange={(e) => setFrontingMode(e.target.value as 'none' | 'nginx' | 'do_lb')}>
            <option value="none">none</option>
            <option value="nginx">nginx</option>
            <option value="do_lb">do_lb</option>
          </select>
          {frontingMode !== 'none' ? <p className="dim">nginx mode requires a domain + TLS email; do_lb terminates TLS at Cloudflare.</p> : null}
        </div>
        {frontingMode !== 'none' ? (
          <div className="field">
            <label>Domain</label>
            <input className="mono" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="e.g. example.com" />
          </div>
        ) : null}
        {frontingMode === 'nginx' ? (
          <div className="field">
            <label>TLS email</label>
            <input className="mono" value={tlsEmail} onChange={(e) => setTlsEmail(e.target.value)} placeholder="e.g. admin@example.com" />
          </div>
        ) : null}
        <div className="field">
          <label>Backup bucket (optional)</label>
          <input className="mono" value={backupBucket} onChange={(e) => setBackupBucket(e.target.value)} placeholder="e.g. my-backups-bucket" />
        </div>
        <div className="field">
          <label>Backup region (optional)</label>
          <input className="mono" value={backupRegion} onChange={(e) => setBackupRegion(e.target.value)} placeholder="e.g. nyc3" />
        </div>
        <p className="error-text">{error}</p>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-amber" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeployDialog({
  stack,
  env,
  onClose,
  onStarted,
}: {
  stack: StackDetail;
  env: EnvDetail;
  onClose: () => void;
  onStarted: (deploymentId: string) => void;
}) {
  const [ref, setRef] = useState('main');
  const [runMigrations, setRunMigrations] = useState(false);
  const [error, setError] = useState('');
  const isProdPair = env.kind === 'production_pair_member';

  const deploy = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string }>(`/environments/${env.id}/deployments`, {
        method: 'POST',
        body: { ref, runMigrations },
      }),
    onSuccess: (d) => onStarted(d.id),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'deploy failed to start'),
  });

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="card dialog" onClick={(e) => e.stopPropagation()}>
        <h2>
          Deploy to <span className="mono">{env.name}</span>
        </h2>
        <p className="dim">
          {stack.repoOwner}/{stack.repoName} → {env.droplets.length} droplet(s). The ref is resolved to a
          commit SHA before anything runs.
        </p>
        <div className="field">
          <label>Branch / tag / SHA</label>
          <input className="mono" value={ref} onChange={(e) => setRef(e.target.value)} autoFocus />
        </div>
        <label className="check-row">
          <input type="checkbox" checked={runMigrations} onChange={(e) => setRunMigrations(e.target.checked)} />
          <span>
            Run database migrations
            {isProdPair ? (
              <span className="migrate-warning">
                blue + gold share the production database — migrations run here are live for the active
                environment immediately. They must be backward-compatible (expand → migrate → contract).
              </span>
            ) : null}
          </span>
        </label>
        <p className="error-text">{error}</p>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-amber" disabled={!ref || deploy.isPending} onClick={() => deploy.mutate()}>
            {deploy.isPending ? 'Starting…' : 'Start deployment'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SwitchDialog({
  stack,
  target,
  onClose,
}: {
  stack: StackDetail;
  target: EnvDetail;
  onClose: () => void;
}) {
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const from = stack.environments.find((e) => e.id === stack.activeProdEnvId);

  const doSwitch = useMutation({
    mutationFn: () =>
      apiFetch(`/stacks/${stack.id}/switch`, {
        method: 'POST',
        body: { targetEnvId: target.id, confirmName: confirm },
      }),
    onSuccess: () => setDone(true),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'switch failed'),
  });

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="card dialog" onClick={(e) => e.stopPropagation()}>
        <h2>Switch production traffic</h2>
        {done ? (
          <>
            <p>
              Switch to <span className="mono">{target.name}</span> is running. Preflight checks, the load
              balancer retarget, and verification happen automatically; if verification fails the previous
              targets are restored.
            </p>
            <div className="dialog-actions">
              <button className="btn btn-amber" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="dim">
              Load balancer will move from{' '}
              <span className="mono">
                {from ? `${from.name} (${from.deployedSha?.slice(0, 7) ?? 'unknown'})` : 'current targets'}
              </span>{' '}
              to{' '}
              <span className="mono">
                {target.name} ({target.deployedSha?.slice(0, 7) ?? 'unknown'})
              </span>
              . The previous environment keeps running, so switching back takes seconds.
            </p>
            <div className="field">
              <label>
                Type <span className="mono">{target.name}</span> to confirm
              </label>
              <input className="mono" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus />
            </div>
            <p className="error-text">{error}</p>
            <div className="dialog-actions">
              <button className="btn btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-amber"
                disabled={confirm !== target.name || doSwitch.isPending}
                onClick={() => doSwitch.mutate()}
              >
                {doSwitch.isPending ? 'Switching…' : `Switch to ${target.name}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
